import { useEffect, useRef, useState } from 'react';

import { modeLabel } from '../format';

export type ModeBadgeMode = 'H_a' | 'H_b' | 'H_c' | 'H_d';

/**
 * ModeBadge — Brief 2A r7b §6.2 canonical (Sprint Visual Agent B).
 *
 * Badge clicável de modo de operação (H_a / H_b / H_c / H_d) com popover
 * descritivo. ADR-029 (4 modos canônicos) + DS v2.3.0 .mode-badge variants.
 *
 * Variants visuais (DS canonical):
 *   - H_a → .mode-badge--you-drive    (atendente dirige)
 *   - H_b → .mode-badge--you-assisted (híbrido)
 *   - H_c → .mode-badge--ai-drives    (IA dirige)
 *   - H_d → .mode-badge--h-d          (delegação manual — global.css custom)
 *
 * Task 59 canonical — quando prop `onChange` está presente, items do popover
 * viram clicáveis e disparam mutation. Quando ausente, popover é read-only.
 */
export function ModeBadge({
  mode,
  onChange,
  isPending = false,
  canSwitchPlaybook = false,
  onSwitchPlaybookClick,
}: {
  mode: ModeBadgeMode;
  onChange?: (mode: ModeBadgeMode) => Promise<void> | void;
  isPending?: boolean;
  /**
   * S7 PR3 (S6 Condução Governada, Q-J8) — quando `true` (cap
   * `playbook-manual-switch` do caller), o popover ganha a ação "Trocar
   * playbook…". Opcional/default `false` — TODOS os usos existentes do
   * ModeBadge ficam intactos sem essa prop (nenhum comportamento novo sem
   * opt-in explícito do caller).
   */
  canSwitchPlaybook?: boolean;
  /** Abre o picker/modal de troca manual (dono do estado vive no caller). */
  onSwitchPlaybookClick?: () => void;
}): JSX.Element {
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Ref no WRAPPER (trigger + popover): se apontasse só para o trigger, o
  // mousedown numa opção do popover contaria como "fora" e desmontava o
  // popover antes do click disparar — onChange nunca executava.
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen]);

  const dsVariant =
    mode === 'H_a'
      ? 'mode-badge--you-drive'
      : mode === 'H_b'
        ? 'mode-badge--you-assisted'
        : mode === 'H_c'
          ? 'mode-badge--ai-drives'
          : 'mode-badge--h-d';

  const handleSelect = async (newMode: ModeBadgeMode): Promise<void> => {
    if (onChange === undefined) return;
    if (newMode === mode) {
      setPopoverOpen(false);
      return;
    }
    try {
      await onChange(newMode);
      setPopoverOpen(false);
    } catch {
      // erro fica no caller via mutation.error — popover permanece aberto
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`mode-badge ${dsVariant}`}
        onClick={() => setPopoverOpen((v) => !v)}
        aria-expanded={popoverOpen}
        aria-haspopup="dialog"
        title={`${extModeLabel(mode)} — clique para detalhes${onChange !== undefined ? ' / alternar' : ''}`}
      >
        {extModeLabel(mode)}
      </button>

      {popoverOpen && (
        <div
          role="dialog"
          aria-label="Modo de operação"
          className="absolute top-full left-0 mt-1 w-64 rounded-md border border-border-subtle bg-surface shadow-lg p-3 z-20 text-xs"
          style={{
            background: 'var(--bg-surface)',
            borderColor: 'var(--border-default)',
            boxShadow: 'var(--shadow-md)',
          }}
        >
          <div className="mb-2">
            <p className="font-semibold" style={{ color: 'var(--fg-1)' }}>
              {extModeLabel(mode)}
            </p>
            <p className="mt-0.5" style={{ color: 'var(--fg-2)' }}>
              {MODE_DESCRIPTIONS[mode]}
            </p>
          </div>
          <div className="space-y-1 border-t pt-2" style={{ borderColor: 'var(--border-subtle)' }}>
            {(['H_a', 'H_b', 'H_d', 'H_c'] as const).map((m) => {
              const isCurrent = m === mode;
              const isClickable = onChange !== undefined && !isCurrent && !isPending;
              const row = (
                <>
                  <span
                    className="text-2xs font-mono px-1 rounded border"
                    style={
                      isCurrent
                        ? {
                            background: 'var(--brand-subtle)',
                            color: 'var(--brand-subtle-fg)',
                            borderColor: 'var(--brand)',
                            fontWeight: 'var(--fw-semibold)',
                          }
                        : {
                            borderColor: 'var(--border-subtle)',
                            color: 'var(--fg-3)',
                          }
                    }
                  >
                    {m}
                  </span>
                  <span style={{ color: 'var(--fg-2)', flex: 1 }}>{MODE_DESCRIPTIONS[m]}</span>
                </>
              );
              if (isClickable) {
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => void handleSelect(m)}
                    className="flex items-center gap-2 w-full text-left px-1 py-0.5 rounded hover:bg-bg-canvas transition"
                    style={{ background: 'transparent' }}
                  >
                    {row}
                  </button>
                );
              }
              return (
                <div key={m} className="flex items-center gap-2 px-1 py-0.5">
                  {row}
                </div>
              );
            })}
          </div>
          {onChange !== undefined && isPending && (
            <p className="text-2xs mt-2 animate-pulse" style={{ color: 'var(--ai-fg)' }}>
              Alternando modo…
            </p>
          )}
          {onChange === undefined && (
            <p className="text-2xs mt-2" style={{ color: 'var(--fg-3)', opacity: 0.7 }}>
              Visualização somente leitura.
            </p>
          )}
          {canSwitchPlaybook && onSwitchPlaybookClick !== undefined && (
            <button
              type="button"
              onClick={() => {
                setPopoverOpen(false);
                onSwitchPlaybookClick();
              }}
              className="w-full text-left px-1 py-1 mt-2 rounded hover:bg-bg-canvas transition"
              style={{
                borderTop: '1px solid var(--border-subtle)',
                color: 'var(--brand)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--fw-semibold)',
              }}
            >
              Trocar playbook…
            </button>
          )}
          <button
            type="button"
            onClick={() => setPopoverOpen(false)}
            className="absolute top-2 right-2 text-sm"
            style={{ color: 'var(--fg-3)' }}
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

const MODE_DESCRIPTIONS: Record<ModeBadgeMode, string> = {
  H_a: 'Humano dirige — IA sugere inline.',
  H_b: 'Híbrido — IA e humano alternando turnos.',
  H_d: 'Delegação manual — atendente delegou explicitamente.',
  H_c: 'IA conduz — com proteção e capability gates.',
};

function extModeLabel(mode: ModeBadgeMode): string {
  if (mode === 'H_d') return 'H_d';
  return modeLabel(mode);
}
